// src/components/CustomerSummary/hooks/derivedStudiesWorkflow.js
// ============================================================================
// BLOCK CS.DER.0 — Derived studies helpers (text -> counts -> workflow distribution)
// ----------------------------------------------------------------------------
// PURPOSE
// - Share one implementation for BOTH:
//     • Studies Started Workflow Distribution (from studiesStarted)
//     • Finalized Studies Workflow Distributions (from finalizedStudies)
//
// NOTES
// - Derived sections are read-only and should not be locked/cleared/regenerated.
// - Percentages are computed as % of GRAND TOTAL (clinic + ship = 100%).
// ============================================================================

const DERIVED_SECTION_KEYS = new Set([
  'studiesStartedWorkflowDistribution',
  'finalizedStudiesWorkflowDistributions',
]);

export function isDerivedSectionKey(key) {
  return DERIVED_SECTION_KEYS.has(String(key || '').trim());
}

function safeStr(v) {
  return v === null || v === undefined ? '' : String(v);
}

function isHeaderLine(line) {
  const s = safeStr(line).trim();
  return s.startsWith('Facility:') || s.startsWith('Date Range:');
}

// ============================================================================
// BLOCK CS.DER.1 — Parse editable base counts from a Studies table-text block
// ----------------------------------------------------------------------------
// Extracts only the 4 editable categories and buckets them by workflow.
// Ignores totals/monthly/grand rows.
// ============================================================================
function parseStudiesForCounts(studiesText, sectionMarkerContains) {
  const raw = safeStr(studiesText);
  const lines = raw.split(/\r?\n/);

  const headerLines = lines.filter((l) => isHeaderLine(l)).map((l) => safeStr(l).trim());

  const idx = lines.findIndex((l) => safeStr(l).toLowerCase().includes(sectionMarkerContains));
  const dataLines = idx >= 0 ? lines.slice(idx + 1) : lines;

  const categories = [
    'Holter',
    'Extended Holter',
    'Cardiac Event Monitoring (CEM)',
    'Mobile Cardiac Telemetry (MCT)',
  ];

  const buckets = {
    'In-clinic connection': Object.fromEntries(categories.map((c) => [c, 0])),
    'Ship-to-patient': Object.fromEntries(categories.map((c) => [c, 0])),
  };

  const toInt = (v) => {
    const n = Number(safeStr(v).replace(/[^\d.-]/g, ''));
    if (!Number.isFinite(n)) {
      return 0;
    }
    return Math.max(0, Math.trunc(n));
  };

  dataLines
    .map((l) => safeStr(l).trim())
    .filter((l) => l.length > 0)
    .forEach((l) => {
      const parts = l.split('\t').map((x) => safeStr(x).trim());
      if (parts.length < 3) {
        return;
      }

      const label = parts[0];
      const wf = parts[1];
      const val = parts[2];

      if (!categories.includes(label)) {
        return;
      }
      if (!Object.prototype.hasOwnProperty.call(buckets, wf)) {
        return;
      }

      buckets[wf][label] = toInt(val);
    });

  const sum = (wf) => categories.reduce((acc, c) => acc + (buckets[wf][c] || 0), 0);

  const inClinicTotal = sum('In-clinic connection');
  const shipTotal = sum('Ship-to-patient');
  const grandTotal = inClinicTotal + shipTotal;

  return { headerLines, categories, buckets, totals: { inClinicTotal, shipTotal, grandTotal } };
}

function fmtPct(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) {
    return '0.00%';
  }
  return `${x.toFixed(2)}%`;
}

// ============================================================================
// BLOCK CS.DER.2 — Build workflow distribution text (public API)
// ----------------------------------------------------------------------------
// Inputs:
// - studiesText: the source section text (studiesStarted OR finalizedStudies)
// - outputTitle: the header line for the derived section
// - sectionMarkerContains: used to locate the start of the source table
// ============================================================================
export function buildWorkflowDistributionTextFromStudiesText({
  studiesText,
  outputTitle,
  sectionMarkerContains,
}) {
  if (!safeStr(studiesText).trim()) {
    return '';
  }

  const parsed = parseStudiesForCounts(studiesText, sectionMarkerContains);
  const { headerLines, categories, buckets, totals } = parsed;

  const denom = totals.grandTotal > 0 ? totals.grandTotal : 0;
  const pctOfGrand = (count) => (denom > 0 ? (Number(count || 0) / denom) * 100 : 0);

  const out = [];
  headerLines.forEach((h) => out.push(h));
  out.push('');
  out.push(`${outputTitle}\t\t`);

  // In-clinic rows
  categories.forEach((c) =>
    out.push(
      `${c}\tIn-clinic connection\t${fmtPct(pctOfGrand(buckets['In-clinic connection'][c]))}`
    )
  );
  out.push(`Total\tIn-clinic connection\t${fmtPct(pctOfGrand(totals.inClinicTotal))}`);

  // Ship-to-patient rows
  categories.forEach((c) =>
    out.push(`${c}\tShip-to-patient\t${fmtPct(pctOfGrand(buckets['Ship-to-patient'][c]))}`)
  );
  out.push(`Total\tShip-to-patient\t${fmtPct(pctOfGrand(totals.shipTotal))}`);

  return out.join('\n');
}
